/* eslint-disable jira/filename-case */

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { LocalOverrides } from '@atlaskit/feature-gate-js-client/persistent-override-adapter';
import { addFeatureFlagAccessed } from '@atlaskit/react-ufo/feature-flags-accessed';
import { captureException } from '@jira/platform__observability-capture-exception/src/index.tsx';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import { UNSAFE_initIfNeeded } from '@jira/platform__utils-feature-gates-unsafe-init/src/index.tsx';

type WindowFlagVars = {
	__MCOND_EVALUATED: Record<string, boolean> | undefined;
	__SSR_SYNC_FG__: undefined | ((gateName: string) => boolean);
	__SSR_SYNC_FG_NO_EXPOSURE__: undefined | ((gateName: string) => boolean);
	_statsigValuesReady: boolean | undefined;
	__SSR_STATSIG_OVERRIDES__:
		| (LocalOverrides & {
				allGatesTrue: boolean;
				allExpValTrue: boolean;
		  })
		| undefined;
};

class ErrorPointingUp extends Error {
	constructor(message: string) {
		super(message);
		// Capture the stack without the error construction
		if (this.stack) {
			const stackLines = this.stack.split('\n');
			// Remove the error creation line and preserve caller stack
			// 1 - checkIfGateExists
			// 2 - featureGate
			// 3 - fg
			this.stack = [...stackLines.slice(3)].join('\n');
		}
	}
}

const checkedGates = new Set<string>();
autoBeforeEachTest(() => {
	checkedGates.clear();
});
const checkIfGateExists = (gateName: string) => {
	// perform early exist to minimize performance impact
	if (checkedGates.has(gateName)) {
		return;
	}
	checkedGates.add(gateName);
	if (
		FeatureGates.checkGate('jfp_magma_jira-report-incorrect-gates', { fireGateExposure: false })
	) {
		if (!FeatureGates.isGateExists(gateName)) {
			const error = new ErrorPointingUp(`Feature gate ${gateName} does not exist`);
			captureException('FeatureGates.isGateExists', error);
			// oxlint-disable-next-line no-console
			console.error(
				`Feature gate ${gateName} does not exist in StatSig. It was archived or hasn't been created yet`,
			);
		}
	}
};

let shouldPerformGateExistenceCheck = true;

/**
 * Disables gate existence check for the duration of the function call.
 * @deprecated reserved for special cases only
 */
export const withoutFeatureExistenceCheck = <T,>(fn: () => T): T => {
	shouldPerformGateExistenceCheck = false;
	const result = fn();
	shouldPerformGateExistenceCheck = true;
	return result;
};

const featureGate: typeof FeatureGates.checkGate = (gateName) => {
	// Handle vendorless scripts use of gates
	UNSAFE_initIfNeeded();

	const value = FeatureGates.checkGate(gateName);
	addFeatureFlagAccessed(gateName, value);

	if (shouldPerformGateExistenceCheck) {
		checkIfGateExists(gateName);
	}

	return value;
};

/**
 * Feature gate helper.
 * @agent-skills: clean-up-feature-gates, feature-gates-experiments
 */
export const fg: typeof FeatureGates.checkGate = (gateName) => {
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const globalVar = (typeof window !== 'undefined'
		? window
		: globalThis) as unknown as WindowFlagVars;

	/**
	 * On server: These overrides are set by the server-side code in jira/src/entry-fs
	 * On client: Check SSR overrides on client-side for shareable debug URLs
	 */
	if (globalVar?.__SSR_STATSIG_OVERRIDES__?.gates?.[gateName] !== undefined) {
		const value = globalVar.__SSR_STATSIG_OVERRIDES__.gates[gateName];
		// Still record the access so overridden gates remain observable (e.g. SSR smoke-test bisect
		// relies on __SSR_FEATURE_FLAGS_ACCESSED__ to see which gates a route evaluated).
		addFeatureFlagAccessed(gateName, value);
		return value;
	}

	if (__SERVER__) {
		// for smoke tests to test when all gates are on
		if (globalVar?.__SSR_STATSIG_OVERRIDES__?.allGatesTrue) {
			addFeatureFlagAccessed(gateName, true);
			return true;
		}

		/**
		 * This is the codepath before _statsigValues is initilaised.
		 * This function is defined within jira/src/entry-fs
		 */
		if (
			typeof globalVar.__SSR_SYNC_FG__ === 'function' &&
			// if global variable is ready, then don't reach out to native
			!globalVar._statsigValuesReady
		) {
			return globalVar.__SSR_SYNC_FG__(gateName);
		}

		return featureGate(gateName);
	}

	if (globalVar.__MCOND_EVALUATED && gateName in globalVar.__MCOND_EVALUATED) {
		const serverResult = globalVar.__MCOND_EVALUATED[gateName];
		const browserResult = featureGate(gateName);
		if (serverResult !== browserResult) {
			let errorMessage = `Conditional import mismatch (returning server result), cond: ${gateName}, ${browserResult} !== ${serverResult} (browser !== server).`;
			if (process.env.NODE_ENV === 'development' || globalThis.BUILD_KEY?.startsWith('MRJF-BB')) {
				errorMessage = `${errorMessage} Do not introduce this error into the codebase. Please see https://hello.atlassian.net/wiki/x/_hjGRAE for details and context.`;
			} else {
				// We can proceed with the server result, but we should log the error to help us debug the issue
				// oxlint-disable-next-line no-console
				console.error(errorMessage);

				captureException(
					'FeatureGates.fg',
					new Error('Conditional import mismatch (returning server result)'),
					{
						teamName: 'uip-app-framework-bundler',
						gateName,
						serverResult: serverResult.toString(),
						browserResult: browserResult.toString(),
					},
				);
			}
		}

		// Return the server-evaluated result
		return globalVar.__MCOND_EVALUATED[gateName];
	}

	return featureGate(gateName);
};

const featureGateNoExposure: typeof FeatureGates.checkGate = (gateName) => {
	if (__SERVER__) {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		const globalVar = (typeof window !== 'undefined'
			? window
			: globalThis) as unknown as WindowFlagVars;

		/**
		 * These overrides are set by the server-side code in jira/src/entry-fs
		 */
		if (globalVar?.__SSR_STATSIG_OVERRIDES__?.gates?.[gateName] !== undefined) {
			return globalVar.__SSR_STATSIG_OVERRIDES__.gates[gateName];
		}

		// for smoke tests to test when all gates are on
		if (globalVar?.__SSR_STATSIG_OVERRIDES__?.allGatesTrue) {
			return true;
		}

		/**
		 * This is the codepath before _statsigValues is initilaised.
		 * This function is defined within jira/src/entry-fs
		 */
		if (
			typeof globalVar?.__SSR_SYNC_FG__ === 'function' &&
			// if global variable is ready, then don't reach out to native
			// API, stay within js runtime as it is faster
			!globalVar._statsigValuesReady
		) {
			return globalVar.__SSR_SYNC_FG__(gateName);
		}
	}

	// oxlint-disable-next-line typescript/consistent-type-assertions
	const globalVar = (typeof window !== 'undefined'
		? window
		: globalThis) as unknown as WindowFlagVars;

	// Check SSR overrides on client-side for shareable debug URLs
	// These overrides are set by SSR from URL params (e.g., ?fg=flag_name:true)
	if (globalVar?.__SSR_STATSIG_OVERRIDES__?.gates?.[gateName] !== undefined) {
		return globalVar.__SSR_STATSIG_OVERRIDES__.gates[gateName];
	}

	// Handle vendorless scripts use of gates
	UNSAFE_initIfNeeded();

	const value = FeatureGates.checkGate(gateName, { fireGateExposure: false });

	return value;
};

/**
 * Feature gate without firing exposures
 *
 * Using this function will avoid sending exposures into observability tools including UFO and Statsig.
 *
 * ---
 *
 * ***You should use the regular `fg()` function in almost all cases.***
 *
 * Please do not use `UNSAFE__fgNoExposure` without discussing with #devinfra-app-framework beforehand.
 *
 * @param gateName feature gate key
 * @returns boolean result of feature gate evaluation
 */
export const UNSAFE__fgNoExposure: typeof FeatureGates.checkGate = (gateName) => {
	// oxlint-disable-next-line typescript/consistent-type-assertions
	const globalVar = (typeof window !== 'undefined'
		? window
		: globalThis) as unknown as WindowFlagVars;

	if (__SERVER__) {
		/**
		 * These overrides are set by the server-side code in jira/src/entry-fs
		 */
		if (globalVar?.__SSR_STATSIG_OVERRIDES__?.gates?.[gateName] !== undefined) {
			return globalVar.__SSR_STATSIG_OVERRIDES__.gates[gateName];
		}

		// for smoke tests to test when all gates are on
		if (globalVar?.__SSR_STATSIG_OVERRIDES__?.allGatesTrue) {
			return true;
		}

		/**
		 * This function is defined within jira/src/entry-fs
		 */
		if (typeof globalVar.__SSR_SYNC_FG_NO_EXPOSURE__ === 'function') {
			return globalVar.__SSR_SYNC_FG_NO_EXPOSURE__(gateName);
		}
	}

	return featureGateNoExposure(gateName);
};
